import React from 'react'
import { Button, Form } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { useState } from 'react';
import UserRequestDetails from '../UserRequestDetails/UserRequestDetails';
import ManageToken from '../../Token';
import { useNavigate } from 'react-router-dom';
import { withAuth } from 'react-oidc-context';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import LogoutUser from '../Logout/Logout';
import logger from '../../logger';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_ROWS = 5000;
const MAX_REQUEST_NAME_LENGTH = 100;
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

class UploadFile extends React.Component<any, any> {
    token: any;
    isAdmin = false;
    constructor(props: any) {
        super(props);
        this.token = new ManageToken();
        this.state = {
            uploadDetails: [{
                "isSuccess": false,
                "errorMessage": null,
                "requestId": 0
             }],
            requestName: '',
            selectedFile: null,
            errorMessage: '',
            accessToken: props.accessToken,
            dataDisplaySpinnerHidden: true,
            isSubmitting: false
        }
        this.handleSubmit = this.handleSubmit.bind(this)
    }

    async componentDidMount() {

    }
   
    onTextChange = (event: any) => {
        this.setState({ requestName: event.target.value, errorMessage: '' })
    };

    // On file select (from the pop up)
    onFileChange = (event: any) => {
        const file: File | null = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
        if (file) {
            logger.info(`[UploadFile] File selected: ${file.name} (${file.size} bytes)`);
        }
        this.setState({ selectedFile: file, errorMessage: '' });
    };

    validateRequestName(name: string): string | null {
        if (!name) {
            return 'Request name should not be empty!';
        }
        if (name.length > MAX_REQUEST_NAME_LENGTH) {
            return `Request name must be ${MAX_REQUEST_NAME_LENGTH} characters or fewer.`;
        }
        return null;
    }


    validateFile(file: File | null): string | null {
        if (!file) {
            return 'Please select a file to upload.';
        }
        const lowerName = file.name.toLowerCase();
        if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
            return 'Please upload an .xlsx or .xls file. See the instructions for the proper file type and format.';
        }
        if (file.size === 0) {
            return 'Selected file is empty.';
        }
        if (file.size > MAX_FILE_BYTES) {
            return `File is too large. Maximum allowed size is ${MAX_FILE_BYTES / (1024 * 1024)} MB.`;
        }
        return null;
    }

    async validateFileContents(file: File): Promise<string | null> {
        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!sheet) {
                return 'Selected file is empty.';
            }
            const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: true });
            if (rows.length === 0) {
                return 'Selected file is empty.';
            }
            const header = (rows[0] || []).slice(0, 4).map((c: any) => String(c).trim().toLowerCase());
            if (header.join('|') !== 'firstname|lastname|email|courseid') {
                return 'Invalid file format. The first row must be FirstName, LastName, Email, CourseId. Please use the New Request template.';
            }

            const seen = new Set<string>();
            let userRows = 0;
            for (let i = 1; i < rows.length; i++) {
                const rowNo = i + 1;
                const [first, last, email, course] = [0, 1, 2, 3].map(c => String(rows[i][c] ?? '').trim());
                if (!first && !last && !email && !course) {
                    continue;
                }
                if (!first) return `FirstName should not be empty! (row ${rowNo})`;
                if (!email) return `Email should not be empty! (row ${rowNo})`;
                if (!EMAIL_REGEX.test(email)) return `Invalid Email at row ${rowNo}!`;
                if (!course) return `CourseID should not be empty! (row ${rowNo})`;
                if (!GUID_REGEX.test(course) || course === EMPTY_GUID) return `Invalid CourseID at row ${rowNo}!`;
                const key = `${email.toLowerCase()}|${course.toLowerCase()}`;
                if (seen.has(key)) return `Duplicate Email and CourseID combination at row ${rowNo}!`;
                seen.add(key);
                userRows++;
                if (userRows > MAX_ROWS) {
                    return `File contains more than ${MAX_ROWS} rows.`;
                }
            }
            if (userRows === 0) {
                return 'Selected file does not contain any user rows.';
            }
            logger.info(`[UploadFile] File validated: ${userRows} user row(s).`);
            return null;
        } catch (error) {
            logger.error('[UploadFile] Unable to read the selected file.', error);
            return 'Unable to read the selected file. Please make sure it is a valid Excel file.';
        }
    }

    async handleSubmit() {
        if (this.state.isSubmitting) {
            return;
        }
        this.setState({ errorMessage: '', dataDisplaySpinnerHidden: false, isSubmitting: true });
        let navigated = false;
        try {
            const requestName = (this.state.requestName ?? '').trim();
            const validationError = this.validateRequestName(requestName)
                ?? this.validateFile(this.state.selectedFile)
                ?? await this.validateFileContents(this.state.selectedFile);
            if (validationError) {
                logger.warn(`[UploadFile] Validation failed: ${validationError}`);
                this.setState({ errorMessage: validationError });
                return;
            }

            const createRequestURL = process.env.REACT_APP_SERVER_URL_CREATE_REQUEST ? process.env.REACT_APP_SERVER_URL_CREATE_REQUEST : '';
            if (!createRequestURL) {
                logger.error('[UploadFile] REACT_APP_SERVER_URL_CREATE_REQUEST is not configured.');
                this.setState({ errorMessage: 'Application is not configured correctly. Please contact support.' });
                return;
            }
            const accessToken = this.token.GetToken();
            if (!accessToken) {
                logger.error('[UploadFile] No access token available.');
                this.setState({ errorMessage: 'Your session has expired. Please sign in again.' });
                return;
            }

            const data = new FormData();
            data.append('file', this.state.selectedFile);
            logger.info(`[UploadFile] Submitting request '${requestName}'.`);
            const res = await fetch(createRequestURL + encodeURIComponent(requestName), {
                method: 'POST',
                body: data,
                headers: {
                    'Authorization': 'Bearer ' + accessToken
                }
            });

            let result: any = null;
            try {
                result = await res.json();
            } catch {
                result = null;
            }

            if (!res.ok) {
                logger.error(`[UploadFile] Upload failed with status ${res.status}.`, result);
                let message = 'Unable to submit the request. Please try again later.';
                if (res.status === 401 || res.status === 403) {
                    message = 'You are not authorized to create requests.';
                } else if (res.status === 413) {
                    message = 'File is too large.';
                }
                this.setState({ errorMessage: message });
                return;
            }

            if (!Array.isArray(result) || result.length === 0) {
                logger.error('[UploadFile] Unexpected response from server.', result);
                this.setState({ errorMessage: 'Unexpected response from the server.' });
                return;
            }

            this.setState({ uploadDetails: result });
            if (result[0].isSuccess) {
                logger.info(`[UploadFile] Request created. RequestId: ${result[0].requestId}`);
                navigated = true;
                this.props.navigation('/courseAssignment/detail/?requestId=' + result[0].requestId, { state: { requestId: result[0].requestId }, replace: true });
            } else {
                logger.warn(`[UploadFile] Server rejected the upload: ${result[0].errorMessage}`);
                this.setState({ errorMessage: result[0].errorMessage || 'Unable to submit the request.' });
            }
        } catch (error) {
            logger.error('[UploadFile] There was an error!', error);
            this.setState({ errorMessage: 'Unable to reach the server. Please try again later.' });
        } finally {
            if (!navigated) {
                this.setState({ dataDisplaySpinnerHidden: true, isSubmitting: false });
            }
        }
    };
    public render() {

        return (
            <div>
                <div className="loading-indicator" hidden={this.state.dataDisplaySpinnerHidden}>
                    <div className="spinner-border text-primary">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
                <Breadcrumbs route="/courseAssignment/requests" name="Course Enrollment" />
                <LogoutUser isAdmin={this.isAdmin} />
                <div className="container mt-5" >
                    <div className="row form-group">
                        <div className="col-lg-4">
                            <div className="bg-light-gray card p-3">
                                <h3>Create a New Request</h3>
                                        <label className="">Request Name:</label>
                                        <input type="text" maxLength={MAX_REQUEST_NAME_LENGTH} onChange={this.onTextChange} value={this.state.requestName} />
                                <br />
                                        <input type="file" accept=".xls,.xlsx" onChange={this.onFileChange} />
                                        <br /><br />
                                        <input type="submit" className="btn btn-primary" id="validate-btn" value="Submit" disabled={this.state.isSubmitting} onClick={this.handleSubmit} />
                                       &nbsp; <label className="text-danger" role="alert">{this.state.errorMessage}</label>

                            </div>
                        </div>
                        <div className="col-md-7 col-lg-5">
                            <label className="control-label"> <b> Instructions: </b>  </label>
                            <li>Download and fill in the <a href="/CommscopeUniversityUtilityTemplate.xlsx"> <b>New Request template</b></a>, in order to upload the proper file type and format.</li>
                            {/*<ol>*/}
                            {/*    <li>*/}
                            {/*        Select the "Choose file..." button to open the file upload window*/}
                            {/*    </li>*/}
                            {/*    <li>*/}
                            {/*        Browse to your prepared .xlsx or .xls request file*/}
                            {/*        <ul>*/}
                            {/*            <li>Download the <a href="https://www.commscope.com/globalassets/digizuite/954364-CommscopeUniversityUtilityTemplate.xlsx"> New Request template</a></li>*/}

                            {/*        </ul>*/}
                            {/*    </li>*/}
                            {/*    <li>*/}
                            {/*        Click 'Submit' to submit the file*/}

                            {/*    </li>*/}
                            {/*</ol>*/}
                        </div>
                    </div>

                </div>
            </div>
        );

    }

}
export function UploadFileRouter() {
    const navigate = useNavigate();
    return (<UploadFile navigation={navigate} />);
}
export default withAuth(UploadFile)
